import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useSnapshot } from 'valtio';
import _ from 'lodash';
import { normalizeNutritionValue } from '../../Utils/nutrition_utils';
import getStateManager from '../../StateManager';
import useSchedulingEnabled from '../../Hooks/useSchedulingEnabled';
import { settingControls, writeTempSettings } from '../settingFunctions';
import { createSettingsDraftStore, restoreSettingsDraft } from '../../Utils/settings_draft';
import {
  activeSchedule,
  normalizeScheduleSettings,
  schedulePath,
  schedulingSettingsReady,
  supportsScheduleProtocol
} from '../../Utils/scheduling_utils';
import {
  expireScheduleSubmission,
  scheduleDraftSubmission,
  keepScheduleDraft,
  schedulePayloadMatches,
  SCHEDULE_SUBMISSION_TIMEOUT_MS,
  submitScheduleSettings
} from '../../Utils/scheduling_transaction';

export function useScheduleSubmissionTimeout(parent, submission) {
  const schedulingEnabled = useSchedulingEnabled();
  useEffect(() => {
    if (!schedulingEnabled || submission?.status !== 'pending') return;
    const timer = setTimeout(() => expireScheduleSubmission(parent),
      Math.max(0, SCHEDULE_SUBMISSION_TIMEOUT_MS - (Date.now() - submission.started_at)));
    return () => clearTimeout(timer);
  }, [parent, submission?.status, submission?.started_at, schedulingEnabled]);
}

// One recipe draft belongs to a controller/zone, regardless of the page that
// edits it. The complete snapshot keeps phase identities stable during readback.
export default function useScheduleDraft({ controller, parent }) {
  const schedulingEnabled = useSchedulingEnabled();
  const liveController = useSnapshot(controller);
  const snapshot = useSnapshot(parent);
  // Leave stored recipes/drafts intact during the release hold. Ordinary tabs
  // must neither restore them nor redirect controls to scheduled phase values.
  const baseline = schedulingEnabled ? normalizeScheduleSettings(snapshot.settings.scheduling) : null;
  const previousReadback = useRef(baseline);
  const ready = schedulingSettingsReady(baseline);
  const retained = schedulingEnabled ? snapshot.temp_settings?.scheduling : undefined;
  const loaded = Boolean(snapshot.schedule_draft_loaded);
  const [preview, setPreview] = useState(null);
  const canRetain = Boolean(liveController.access_rights?.scheduling?.write);
  const values = canRetain ? preview ?? (loaded && retained
    ? restoreSettingsDraft(baseline, retained, (current, draft, key) =>
        key === 'lighting' || Array.isArray(draft) ? _.cloneDeep(draft) : undefined)
    : _.cloneDeep(baseline)) : _.cloneDeep(baseline);
  const submission = schedulingEnabled ? snapshot.schedule_submission : undefined;
  // Commit confirmation can precede runtime activation. Keep the submitted
  // editor phase during that gap without treating it as the active phase.
  const awaitingActivation = submission?.status === 'confirmed' &&
    submission.expected_revision === baseline?.schedule_values?.recipe_revision &&
    baseline?.schedule_meta?.schedule_enabled &&
    baseline.schedule_values.active_recipe_revision !== submission.expected_revision;
  const confirmedPin = awaitingActivation && submission.submitted_draft?._editing_phase
    ? { ...submission.submitted_draft._editing_phase, recipe_revision: submission.expected_revision }
    : undefined;
  const pin = retained?._editing_phase ?? preview?._editing_phase ?? confirmedPin;
  const current = activeSchedule(liveController, parent, baseline);
  const phases = values?.schedule_values?.scheduling_values ?? [];
  const phaseId = pin?.phase_id ?? current?.phaseId;
  const phaseIndex = phases.findIndex((phase) => phase.phase_id === phaseId);
  const revision = baseline?.schedule_values?.recipe_revision;
  useEffect(() => setPreview(null), [revision, retained, parent]);
  const draftRevision = retained?.schedule_values?.recipe_revision ?? retained?.schedule_meta?.recipe_revision ?? pin?.recipe_revision;
  const ownConfirmation = submission?.status === 'confirmed' &&
    submission.expected_revision === revision && draftRevision === submission.base_revision;
  const stale = Boolean((retained && draftRevision !== revision && !ownConfirmation) ||
    (pin && (phaseIndex < 0 || (pin.recipe_revision !== revision && !ownConfirmation))));
  // Field paths use the draft's order; authoritative baselines use stable IDs.
  const editableBaseline = baseline && {
    ...baseline,
    schedule_values: { ...baseline.schedule_values, scheduling_values: phases.map((phase) =>
      baseline.schedule_values?.scheduling_values.find((saved) => saved.phase_id === phase.phase_id)) }
  };
  const writable = ready && loaded && supportsScheduleProtocol(liveController, values) &&
    Boolean(liveController.access_rights?.scheduling?.write);
  const store = useMemo(
    () => createSettingsDraftStore(parent, 'scheduling', () => writeTempSettings(parent)), [parent]);
  const latestValues = () => restoreSettingsDraft(
    normalizeScheduleSettings(parent.settings.scheduling),
    parent.schedule_draft_loaded ? parent.temp_settings.scheduling ?? {} : {},
    (current, draft, key) => key === 'lighting' || Array.isArray(draft) ? _.cloneDeep(draft) : undefined
  );
  const write = useCallback((next) => {
    parent.schedule_draft_loaded = true;
    store.write(next);
  }, [parent, store]);
  useEffect(() => {
    if (!ready) return;
    const previous = previousReadback.current;
    previousReadback.current = _.cloneDeep(baseline);
    const ownReadback = submission?.status === 'confirmed' && submission.expected_revision === revision;
    if (loaded && liveController.access_rights?.scheduling?.write &&
        schedulingSettingsReady(previous) && !retained && !ownReadback &&
        !schedulePayloadMatches(previous, baseline)) {
      // A recipe is one revisioned draft. Existing conflict/review and Reset
      // actions resolve remote replacement without matching phases by position.
      write(previous);
    }
  }, [baseline, loaded, ready, retained, revision, write, submission?.status,
    submission?.expected_revision, liveController.access_rights?.scheduling?.write]);
  const pinNext = (next, entries) => {
    if (!next._editing_phase && !entries.some(([path]) => path === '_editing_phase')) {
      const editedIndex = entries.map(([path]) =>
        /^schedule_values\.scheduling_values\[(\d+)\]/.exec(path)?.[1]
      ).find((index) => index !== undefined);
      const phase = editedIndex === undefined ? phases[phaseIndex] : phases[Number(editedIndex)];
      if (phase) next._editing_phase = { phase_id: phase.phase_id, recipe_revision: revision };
    }
    return next;
  };
  const draft = {
    read: store.read,
    edit(entries) {
      const next = latestValues();
      entries.forEach(([path, value]) => {
        if (path === null) Object.assign(next, _.cloneDeep(value));
        else _.set(next, path, _.cloneDeep(value));
      });
      write(pinNext(next, entries));
    },
    remove(paths) {
      const next = latestValues();
      paths.forEach((path) => _.set(next, path, _.cloneDeep(_.get(editableBaseline, path))));
      write(next);
    },
    clear: () => { store.clear(); parent.schedule_draft_loaded = true; },
    restore: () => {
      parent.schedule_draft_loaded = true;
      return values;
    }
  };
  const canEditRecipe = () => {
    const currentSettings = normalizeScheduleSettings(parent.settings.scheduling);
    const retainedPin = parent.temp_settings.scheduling?._editing_phase ?? preview?._editing_phase;
    const currentRevision = currentSettings?.schedule_values?.recipe_revision;
    const retainedRecipe = parent.temp_settings.scheduling;
    const retainedRevision = retainedRecipe?.schedule_values?.recipe_revision ??
      retainedRecipe?.schedule_meta?.recipe_revision ?? retainedPin?.recipe_revision;
    const attempt = parent.schedule_submission;
    const ownCommittedRevision = attempt?.status === 'confirmed' &&
      attempt.expected_revision === currentRevision && retainedRevision === attempt.base_revision;
    return writable && !stale && Boolean(controller.access_rights?.scheduling?.write) &&
      schedulingSettingsReady(currentSettings) &&
      (!retainedRecipe || retainedRevision === currentRevision || ownCommittedRevision) &&
      (!retainedPin || retainedPin.recipe_revision === currentRevision || ownCommittedRevision);
  };
  let previewEntries;
  const controls = settingControls(controller, parent, 'scheduling', values,
    (next) => setPreview(previewEntries ? pinNext(next, previewEntries) : null), {
    draft,
    onChange: (change) => {
      previewEntries = change.type === 'edit' && !change.saveTemp ? change.entries : null;
    },
    normalizeValue: normalizeNutritionValue,
    getBaseline: () => editableBaseline,
    canEdit: canEditRecipe
  });
  const resetField = controls.resetSetting;
  controls.resetSetting = (path) => {
    if (!canEditRecipe()) return;
    if (path.startsWith('schedule_values.scheduling_values[') && _.get(editableBaseline, path) === undefined) return;
    resetField(path);
  };
  const resetControls = controls.resetSettings;
  const reset = () => {
    delete parent.schedule_submission;
    resetControls();
  };
  const review = () => {
    // An explicit review can keep the same named phase, never its old position.
    if (pin && !baseline?.schedule_values?.scheduling_values.some(
      (phase) => phase.phase_id === pin.phase_id
    )) return false;
    return keepScheduleDraft(parent);
  };
  const save = () => {
    if (!canEditRecipe()) return false;
    return submitScheduleSettings({
      values: latestValues(), parent, controller, stateManager: getStateManager(), tab_id: 'scheduling'
    });
  };
  controls.saveSettings = save;
  controls.resetSettings = reset;
  useEffect(() => {
    if (!ownConfirmation) return;
    const next = _.cloneDeep(parent.temp_settings.scheduling);
    next.schedule_meta.recipe_revision = revision;
    next.schedule_values.recipe_revision = revision;
    if (next._editing_phase) next._editing_phase.recipe_revision = revision;
    store.write(next);
  }, [ownConfirmation, parent, revision, store]);
  useScheduleSubmissionTimeout(parent, submission);
  const context = (current || pin) && phaseIndex >= 0 ? {
    ...current,
    phaseId,
    phaseNumber: phases.filter((phase) => !phase._deleted).findIndex((phase) => phase.phase_id === phaseId) + 1 || phaseIndex + 1,
    phaseName: phases[phaseIndex].name_phase_full || phases[phaseIndex].name_phase_short,
    phase: phases[phaseIndex],
    metadata: values.schedule_meta,
    dirty: Boolean(retained),
    phaseIndex,
    href: schedulePath(controller, parent, { phaseId, recipeRevision: pin?.recipe_revision ?? revision }),
    scheduleHref: schedulePath(controller, parent)
  } : null;
  return {
    controller, parent, values, baseline, controls, save, reset, review,
    ready, loaded, stale, writable, submission: schedulingEnabled ? scheduleDraftSubmission(snapshot) : undefined, context, phaseIndex,
    dirty: Boolean(retained), store
  };
}
